import { useRef, useState } from 'react';
import { Loader2, Building2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  useEnterpriseEmulation,
  EnterpriseEmulationStartError,
} from '@/hooks/useEnterpriseEmulation';
import { captureError } from '@/lib/sentry';
import {
  REASON_MIN,
  validateEmulationForm,
  type EmulationFormErrors,
} from '../emulation-validation';

interface EnterpriseEmulateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerId: string;
  targetUserId: string;
  targetEmail: string;
}

/**
 * Map raw server error codes / transport errors to a clear, actionable message.
 * The most common real failure is a mistyped password (password_verification_failed) —
 * surfaced plainly so a failed attempt never reads as "nothing happened".
 */
function friendlyStartError(err: unknown): string {
  if (err instanceof EnterpriseEmulationStartError) {
    switch (err.code) {
      case 'password_verification_failed':
        return "That password didn't match. Re-enter your password and try again.";
      case 'caller_not_admin_of_partner':
        return "You don't have permission to emulate members of this enterprise.";
      case 'target_not_member_of_partner':
        return 'That person is no longer an active member of this enterprise.';
      case 'target_is_partner_admin':
      case 'target_is_super_admin':
        return 'That user cannot be emulated.';
      case 'cannot_emulate_self':
        return 'You cannot emulate your own account.';
      case 'open_session_exists':
        return 'An earlier emulation session is still open. Recover it and retry below.';
      case 'validation':
      case 'reason_too_short':
        return 'Please provide a reason of at least 10 characters.';
    }
    return err.message || 'Failed to start emulation.';
  }
  if (err instanceof Error) {
    if (err.name === 'FunctionsFetchError' || /failed to send a request/i.test(err.message)) {
      return "Couldn't reach the server. Check your connection and try again.";
    }
    return err.message || 'Failed to start emulation.';
  }
  return 'Failed to start emulation.';
}

/**
 * Pre-flight modal for starting an ENTERPRISE-admin emulation session.
 * Simplified relative to the super-admin EmulateUserModal (spec §6.3):
 *   - Reason for access (required, ≥10 chars)
 *   - Admin password (re-verified server-side)
 *   - NO read-only toggle (this tier is always full read-write)
 *   - NO suppress-notification UI (this tier never notifies)
 *
 * The Start button stays enabled while the form is invalid; on click we
 * validate, render inline field errors, focus the first invalid field, and
 * return before the RPC. On a 409 open_session_exists, shows a "Recover stuck
 * session and retry" affordance that cancels the orphan row then re-attempts.
 */
export function EnterpriseEmulateModal({
  open,
  onOpenChange,
  partnerId,
  targetUserId,
  targetEmail,
}: EnterpriseEmulateModalProps): JSX.Element {
  const { toast } = useToast();
  const { start, cancel, isStarting } = useEnterpriseEmulation();

  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<EmulationFormErrors>({});
  const [error, setError] = useState<string | null>(null);
  // When start returns 409 open_session_exists, surface a recovery
  // affordance: the admin has an orphaned audit row from a previous
  // attempt where the session swap never landed (one-open-per-admin wedge).
  const [recoveryNeeded, setRecoveryNeeded] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);

  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setReason('');
    setPassword('');
    setFieldErrors({});
    setError(null);
    setRecoveryNeeded(false);
    setIsRecovering(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  // Live clearing: drop a field's error the moment it becomes valid. We never
  // ADD an error on change — errors only appear on a Start click.
  const handleReasonChange = (value: string) => {
    setReason(value);
    setFieldErrors((prev) =>
      prev.reason && value.trim().length >= REASON_MIN
        ? { ...prev, reason: undefined }
        : prev,
    );
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    setFieldErrors((prev) =>
      prev.password && value.length > 0 ? { ...prev, password: undefined } : prev,
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isStarting || isRecovering) return;

    const errors = validateEmulationForm({ reason, password });
    if (errors.reason || errors.password) {
      setFieldErrors(errors);
      if (errors.reason) {
        reasonRef.current?.focus();
      } else if (errors.password) {
        passwordRef.current?.focus();
      }
      return;
    }

    setFieldErrors({});
    setError(null);
    setRecoveryNeeded(false);
    try {
      await start({
        partner_id: partnerId,
        target_user_id: targetUserId,
        target_email: targetEmail,
        reason: reason.trim(),
        password,
      });
      toast({
        title: 'Emulation started',
        description: `Now viewing the app as ${targetEmail}.`,
      });
      handleOpenChange(false);
    } catch (err) {
      setError(friendlyStartError(err));
      if (
        err instanceof EnterpriseEmulationStartError &&
        err.code === 'open_session_exists'
      ) {
        setRecoveryNeeded(true);
      } else {
        captureError(err as Error, { feature: 'enterprise-emulation-start' });
      }
    }
  };

  const handleRecover = async () => {
    setIsRecovering(true);
    try {
      await cancel();
      // Recovered the orphan — immediately re-attempt the start.
      await start({
        partner_id: partnerId,
        target_user_id: targetUserId,
        target_email: targetEmail,
        reason: reason.trim(),
        password,
      });
      toast({
        title: 'Emulation started',
        description: `Now viewing the app as ${targetEmail}.`,
      });
      handleOpenChange(false);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to recover the previous session.';
      setError(msg);
      captureError(err as Error, { feature: 'enterprise-emulation-cancel' });
    } finally {
      setIsRecovering(false);
    }
  };

  const reasonChars = reason.trim().length;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-amber-600" />
            Emulate member
          </DialogTitle>
          <DialogDescription>
            Sign in as <span className="font-mono">{targetEmail}</span>. You will have
            full access to this member&rsquo;s account. The session is fully
            audit-logged, and the member sees a banner if they sign in while it is
            active.
          </DialogDescription>
        </DialogHeader>

        {/* noValidate: native HTML5 constraint validation (required/minLength on
            the fields below) would otherwise swallow the submit event for empty
            fields, so handleSubmit's custom inline validation would never run.
            We run validateEmulationForm on submit instead. */}
        <form noValidate onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="ent-emulate-reason">Reason for access (required)</Label>
            <Textarea
              id="ent-emulate-reason"
              ref={reasonRef}
              value={reason}
              onChange={(e) => handleReasonChange(e.target.value)}
              placeholder="e.g., Member asked us to set up their first campaign"
              required
              minLength={REASON_MIN}
              rows={2}
              className="resize-none"
            />
            {fieldErrors.reason ? (
              <p className="text-xs text-destructive">{fieldErrors.reason}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Minimum {REASON_MIN} characters. Recorded in the audit log.
                {reasonChars < REASON_MIN && (
                  <span className="ml-1 tabular-nums">
                    ({reasonChars} / {REASON_MIN})
                  </span>
                )}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="ent-emulate-password">Confirm your password</Label>
            <PasswordInput
              id="ent-emulate-password"
              ref={passwordRef}
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              autoComplete="current-password"
              required
            />
            {fieldErrors.password ? (
              <p className="text-xs text-destructive">{fieldErrors.password}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Re-verified server-side. Required every time.
              </p>
            )}
          </div>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive space-y-2">
              <div>{error}</div>
              {recoveryNeeded && (
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleRecover}
                    disabled={isRecovering}
                  >
                    {isRecovering ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                        Recovering…
                      </>
                    ) : (
                      'Recover stuck session and retry'
                    )}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    An earlier emulation didn&rsquo;t fully start.
                  </span>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={isStarting || isRecovering}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isStarting || isRecovering}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              {isStarting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Starting…
                </>
              ) : (
                'Start emulation'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
